import { Container, Sprite, Texture } from 'pixi.js';
import { DESIGN, type QualityProfile } from '../core/runtime.js';

interface Particle {
  sprite: Sprite;
  vx: number;
  vy: number;
  gravity: number;
  drag: number;
  spin: number;
  life: number;
  maxLife: number;
  scaleFrom: number;
  scaleTo: number;
  alphaFrom: number;
  fadeIn: number;
}

export interface BurstOptions {
  count?: number;
  colors?: readonly number[];
  speed?: number;
  speedVariance?: number;
  gravity?: number;
  drag?: number;
  life?: number;
  lifeVariance?: number;
  size?: number;
  sizeVariance?: number;
  shrink?: number;
  spread?: number;
  angle?: number;
  additive?: boolean;
  shape?: 'spark' | 'shard' | 'ring';
}

/**
 * Pooled particle system.
 *
 * One texture per shape and one container per blend mode, so the whole system
 * collapses into two or three draw calls no matter how many particles are live.
 * Sprites are recycled rather than created: at ninety eliminations a minute the
 * garbage from naive allocation is what actually drops frames, not the drawing.
 *
 * The particle budget from the quality profile is a hard cap. When it is hit,
 * the oldest particles are retired early - a slightly short-lived spark is
 * invisible, a stalled frame is not.
 */
export class ParticleEngine {
  readonly view = new Container();

  private readonly additiveLayer = new Container();
  private readonly normalLayer = new Container();
  private readonly pool: Sprite[] = [];
  private readonly live: Particle[] = [];

  private readonly sparkTexture: Texture;
  private readonly shardTexture: Texture;
  private readonly ringTexture: Texture;

  constructor(private readonly quality: QualityProfile) {
    this.sparkTexture = createSparkTexture();
    this.shardTexture = createShardTexture();
    this.ringTexture = createRingTexture();

    this.additiveLayer.blendMode = 'add';
    this.view.addChild(this.normalLayer, this.additiveLayer);
    this.view.eventMode = 'none';
    this.normalLayer.eventMode = 'none';
    this.additiveLayer.eventMode = 'none';
  }

  get liveCount(): number {
    return this.live.length;
  }

  /** Radial burst. The workhorse: eliminations, strikes, reveals. */
  burst(x: number, y: number, options: BurstOptions = {}): void {
    const {
      count = 24,
      colors = [0x38bdf8, 0xffc94d, 0xffffff],
      speed = 320,
      speedVariance = 0.6,
      gravity = 420,
      drag = 0.86,
      life = 1.1,
      lifeVariance = 0.4,
      size = 14,
      sizeVariance = 0.5,
      shrink = 0.15,
      spread = Math.PI * 2,
      angle = 0,
      additive = true,
      shape = 'spark',
    } = options;

    const total = Math.max(1, Math.round(count * this.quality.particleScale));
    const texture =
      shape === 'shard'
        ? this.shardTexture
        : shape === 'ring'
          ? this.ringTexture
          : this.sparkTexture;

    for (let i = 0; i < total; i++) {
      const theta = angle + (Math.random() - 0.5) * spread;
      const magnitude = speed * (1 + (Math.random() - 0.5) * 2 * speedVariance);
      const scale = (size * (1 + (Math.random() - 0.5) * 2 * sizeVariance)) / 64;

      this.spawn({
        texture,
        x,
        y,
        tint: colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff,
        vx: Math.cos(theta) * magnitude,
        vy: Math.sin(theta) * magnitude,
        gravity,
        drag,
        spin: (Math.random() - 0.5) * 8,
        life: life * (1 + (Math.random() - 0.5) * 2 * lifeVariance),
        scaleFrom: scale,
        scaleTo: scale * shrink,
        alphaFrom: 1,
        fadeIn: 0,
        additive,
      });
    }
  }

  /** A single firework: rising trail then a wide, slow, gravity-bound bloom. */
  firework(x: number, y: number, color: number): void {
    this.burst(x, y, {
      count: 70,
      colors: [color, 0xffffff, 0xffe9a8],
      speed: 520,
      speedVariance: 0.55,
      gravity: 260,
      drag: 0.9,
      life: 2.1,
      lifeVariance: 0.35,
      size: 11,
      shrink: 0.05,
      additive: true,
    });
    this.burst(x, y, {
      count: 1,
      colors: [color],
      speed: 0,
      gravity: 0,
      life: 0.55,
      size: 260,
      shrink: 3.4,
      shape: 'ring',
      additive: true,
    });
  }

  /** Ribbon confetti falling across the whole frame. */
  confetti(
    count = 160,
    colors: readonly number[] = [0xffc94d, 0x38bdf8, 0xffffff, 0x43e08a],
  ): void {
    const total = Math.max(1, Math.round(count * this.quality.particleScale));
    for (let i = 0; i < total; i++) {
      const scale = (10 + Math.random() * 12) / 64;
      this.spawn({
        texture: this.shardTexture,
        x: Math.random() * DESIGN.width,
        y: -60 - Math.random() * 400,
        tint: colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff,
        vx: (Math.random() - 0.5) * 120,
        vy: 120 + Math.random() * 220,
        gravity: 90,
        drag: 0.99,
        spin: (Math.random() - 0.5) * 14,
        life: 4.5 + Math.random() * 2.5,
        scaleFrom: scale,
        scaleTo: scale,
        alphaFrom: 1,
        fadeIn: 0.2,
        additive: false,
      });
    }
  }

  /** Slow upward embers. Used under ceremonies to keep the frame alive. */
  embers(count = 14, colors: readonly number[] = [0xffc94d, 0x38bdf8]): void {
    const total = Math.max(1, Math.round(count * this.quality.particleScale));
    for (let i = 0; i < total; i++) {
      const scale = (6 + Math.random() * 10) / 64;
      this.spawn({
        texture: this.sparkTexture,
        x: Math.random() * DESIGN.width,
        y: DESIGN.height + 30,
        tint: colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff,
        vx: (Math.random() - 0.5) * 40,
        vy: -(40 + Math.random() * 70),
        gravity: -8,
        drag: 0.995,
        spin: 0,
        life: 5 + Math.random() * 3,
        scaleFrom: scale,
        scaleTo: scale * 0.2,
        alphaFrom: 0.9,
        fadeIn: 0.6,
        additive: true,
      });
    }
  }

  update(deltaSeconds: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const particle = this.live[i]!;
      particle.life -= deltaSeconds;

      if (particle.life <= 0) {
        this.retire(i);
        continue;
      }

      const dragFactor = Math.pow(particle.drag, deltaSeconds * 60);
      particle.vx *= dragFactor;
      particle.vy = particle.vy * dragFactor + particle.gravity * deltaSeconds;

      const sprite = particle.sprite;
      sprite.x += particle.vx * deltaSeconds;
      sprite.y += particle.vy * deltaSeconds;
      sprite.rotation += particle.spin * deltaSeconds;

      const t = 1 - particle.life / particle.maxLife;
      const scale = particle.scaleFrom + (particle.scaleTo - particle.scaleFrom) * t;
      sprite.scale.set(scale);

      if (particle.fadeIn > 0 && t < particle.fadeIn) {
        sprite.alpha = particle.alphaFrom * (t / particle.fadeIn);
      } else {
        const fadeT = particle.fadeIn > 0 ? (t - particle.fadeIn) / (1 - particle.fadeIn) : t;
        sprite.alpha = particle.alphaFrom * (1 - fadeT * fadeT);
      }
    }
  }

  clear(): void {
    for (let i = this.live.length - 1; i >= 0; i--) this.retire(i);
  }

  private spawn(input: {
    texture: Texture;
    x: number;
    y: number;
    tint: number;
    vx: number;
    vy: number;
    gravity: number;
    drag: number;
    spin: number;
    life: number;
    scaleFrom: number;
    scaleTo: number;
    alphaFrom: number;
    fadeIn: number;
    additive: boolean;
  }): void {
    // Budget guard: retire the oldest rather than refuse the newest, so a big
    // moment always looks big even when the frame is already busy.
    while (this.live.length >= this.quality.particleBudget) this.retire(0);

    const sprite = this.pool.pop() ?? new Sprite();
    sprite.texture = input.texture;
    sprite.anchor.set(0.5);
    sprite.x = input.x;
    sprite.y = input.y;
    sprite.tint = input.tint;
    sprite.alpha = input.fadeIn > 0 ? 0 : input.alphaFrom;
    sprite.rotation = Math.random() * Math.PI * 2;
    sprite.scale.set(input.scaleFrom);
    sprite.visible = true;

    const layer = input.additive ? this.additiveLayer : this.normalLayer;
    layer.addChild(sprite);

    this.live.push({
      sprite,
      vx: input.vx,
      vy: input.vy,
      gravity: input.gravity,
      drag: input.drag,
      spin: input.spin,
      life: input.life,
      maxLife: input.life,
      scaleFrom: input.scaleFrom,
      scaleTo: input.scaleTo,
      alphaFrom: input.alphaFrom,
      fadeIn: input.fadeIn,
    });
  }

  private retire(index: number): void {
    const particle = this.live[index];
    if (!particle) return;
    this.live.splice(index, 1);
    particle.sprite.removeFromParent();
    particle.sprite.visible = false;
    this.pool.push(particle.sprite);
  }
}

function createCanvas(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas is unavailable - cannot build particle textures');
  return { canvas, ctx };
}

/** Soft round spark. Tinted per particle, so it must be white. */
function createSparkTexture(): Texture {
  const { canvas, ctx } = createCanvas(64);
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.35, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  return Texture.from(canvas);
}

/** Flat rectangle for confetti ribbons and debris shards. */
function createShardTexture(): Texture {
  const { canvas, ctx } = createCanvas(64);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(20, 4, 24, 56);
  return Texture.from(canvas);
}

/** Thin ring for shockwave flashes. */
function createRingTexture(): Texture {
  const { canvas, ctx } = createCanvas(64);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.arc(32, 32, 26, 0, Math.PI * 2);
  ctx.stroke();
  return Texture.from(canvas);
}
